import React, { use, useEffect, useMemo, useState } from "react";
import "./EncuestaChatBot.scss";
import { useDispatch } from "react-redux";
import { responderEncuestaChatbot } from "../../actions/usuarioActions";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const buildUrl = (path) =>
  `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;

const LISTAR_PREGUNTAS_URL = buildUrl("/admin/listarEncuestas?encuestaId=1");

const OPTIONS = [
  { label: "Totalmente en desacuerdo", value: 1 },
  { label: "En desacuerdo", value: 2 },
  { label: "Ni de acuerdo ni en desacuerdo", value: 3 },
  { label: "De acuerdo", value: 4 },
  { label: "Totalmente de acuerdo", value: 5 },
];

const EncuestaChatBot = ({ open, idUsuario, setOpen, onCloseAfterSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({}); // { [idpregunta]: 1..5 }
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch();

  const allAnswered = useMemo(() => {
    if (!preguntas.length) return false;
    return preguntas.every((p) => respuestas[p.idpregunta] != null);
  }, [preguntas, respuestas]);

  const faltantes = useMemo(() => {
    if (!preguntas.length) return 0;
    return preguntas.filter((p) => respuestas[p.idpregunta] == null).length;
  }, [preguntas, respuestas]);

  const cerrarEncuesta = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;

    // bloquear ESC
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    setError("");
    setPreguntas([]);
    setRespuestas({});

    const fetchPreguntas = async () => {
      try {
        setLoading(true);

        const res = await fetch(LISTAR_PREGUNTAS_URL); // ✅ sin headers

        if (!res.ok) throw new Error("No se pudieron obtener las preguntas.");
        const data = await res.json();

        // ✅ AQUÍ VA lo que no entendías:
        const raw = Array.isArray(data)
          ? data
          : data?.preguntas || data?.data || [];

        const preguntasNormalizadas = raw.map((p) => ({
          idpregunta:
            p.idpregunta ??
            p.id_pregunta ??
            p.idPregunta ??
            p.id ??
            p.preguntaId,
          pregunta: p.pregunta ?? p.descripcion ?? p.texto ?? p.nombre,
        }));

        setPreguntas(preguntasNormalizadas);
      } catch (e) {
        setError(e.message || "Error cargando preguntas.");
      } finally {
        setLoading(false);
      }
    };

    fetchPreguntas();
  }, [open]);

  const setRespuesta = (idpregunta, value) => {
    setRespuestas((prev) => ({ ...prev, [idpregunta]: value }));
  };

  const enviar = async () => {
    if (!allAnswered) return;

    try {
      setSending(true);
      setError("");

      if (!idUsuario) {
        setError("Vuelve a iniciar sesión por favor.");
        return;
      }

      const payload = {
        id_usuario: String(idUsuario),
        data: preguntas.map((p) => ({
          id_pregunta: Number(p.idpregunta),
          respuesta: Number(respuestas[p.idpregunta]),
        })),
      };

      const res = await dispatch(responderEncuestaChatbot(payload));

      onCloseAfterSubmit();
    } catch (e) {
      setError(
        "El servicio no está disponible en este momento. Inténtalo más tarde."
      );
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="ecb-backdrop" onClick={(e) => e.stopPropagation()}>
      <div className="ecb-card" role="dialog" aria-modal="true">
        <div className="ecb-header">
          <div className="d-flex justify-content-between align-items-center">
            <div className="ecb-title">Encuesta obligatoria</div>

            <button
              type="button"
              className="chatbot-close btn btn-link"
              onClick={cerrarEncuesta}
            >
              Cerrar
            </button>
          </div>
          <div className="ecb-subtitle">
            Para usar el chatbot responde las preguntas y presiona <b>Enviar</b>{" "}
            para continuar.
          </div>
        </div>

        <div className="ecb-body">
          {loading && <div className="ecb-loading">Cargando preguntas...</div>}
          {error && <div className="ecb-error">❌ {error}</div>}

          {!loading &&
            !error &&
            preguntas.map((p, idx) => (
              <div className="ecb-q" key={p.idpregunta}>
                <div className="ecb-qtext">
                  {idx + 1}. {p.pregunta}
                </div>

                <div className="ecb-likert">
                  {/* cabecera: textos */}
                  <div className="ecb-likert-head">
                    {OPTIONS.map((opt) => (
                      <div
                        key={opt.value}
                        className="ecb-likert-col"
                        title={opt.label}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>

                  {/* fila: radios */}
                  <div className="ecb-likert-row">
                    {OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="ecb-likert-cell"
                        aria-label={opt.label}
                      >
                        <input
                          type="radio"
                          name={`q_${p.idpregunta}`} // ✅ cada pregunta su propio grupo
                          value={opt.value}
                          checked={respuestas[p.idpregunta] === opt.value}
                          onChange={() => setRespuesta(p.idpregunta, opt.value)} // ✅ sigue guardando 1..5
                        />
                        <span className="ecb-radio" aria-hidden="true" />
                        <span className="ecb-sr-only">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}
        </div>

        <div className="ecb-footer">
          {!allAnswered && !loading && (
            <div className="ecb-hint">
              Te faltan <b>{faltantes}</b> pregunta(s) por responder.
            </div>
          )}

          <button
            type="button"
            className="ecb-submit"
            disabled={!allAnswered || sending || loading}
            onClick={enviar}
          >
            {sending ? "Enviando..." : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EncuestaChatBot;
